import React, { type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';
import {
    User,
    Shield,
    Bell,
    Globe,
    ChevronRight,
    Settings as SettingsIcon
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const SettingsLayout: React.FC = () => {
    const location = useLocation();
    const { user, isLoading } = useAuth();

    const menuItems = [
        {
            title: 'Profile',
            path: '/settings/profile',
            icon: User,
            description: 'Personal information'
        },
        {
            title: 'Account & Security',
            path: '/settings/security',
            icon: Shield,
            description: 'Password, 2FA, phone verification'
        },
        {
            title: 'Notifications',
            path: '/settings/notifications',
            icon: Bell,
            description: 'Email and push notifications'
        },
        {
            title: 'Preferences',
            path: '/settings/preferences',
            icon: Globe,
            description: 'Language, region, display'
        }
    ];

    const isActive = (path: string) => {
        return location.pathname.startsWith(path);
    };

    if (isLoading) return <div>Loading...</div>;
    if (!user) return <div>User not found</div>;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-6">
                        <div className="flex items-center">
                            <SettingsIcon className="h-8 w-8 text-primary-600 mr-3" />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
                                <p className="text-sm text-gray-600 mt-1">
                                    Manage your account preferences and security
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Navigation */}
                    <div className="lg:w-64">
                        <nav className="space-y-1">
                            {menuItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`
                    flex items-center justify-between px-4 py-3 rounded-lg transition-colors
                    ${isActive(item.path)
                                            ? 'bg-primary-50 text-primary-700 border border-primary-100'
                                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                        }
                  `}
                                >
                                    <div className="flex items-center">
                                        <item.icon className={`h-5 w-5 mr-3 ${isActive(item.path) ? 'text-primary-600' : 'text-gray-400'
                                            }`} />
                                        <div>
                                            <div className="font-medium">{item.title}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {item.description}
                                            </div>
                                        </div>
                                    </div>
                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                </Link>
                            ))}
                        </nav>

                        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                                <span className="font-medium">Tip:</span> Verify your phone number
                                for enhanced security and delivery updates.
                            </p>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1">
                        <div className="bg-white rounded-lg shadow-sm border">
                            <Outlet context = {{ user }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsLayout;